import { DEFAULT_TYPOGRAPHY } from "../constants/typography.js";

export const INTELICAR_THEME = {
  id: "intellicar",
  name: "Intellicar",
  accent: { name: "Intellicar", hex: "#E40046", activeBg: "#FFE6EE" },
  brand: {
    primary: "#E40046",
    primaryHover: "#D1203C",
    primaryDark: "#C20039",
    primaryTint: "#FFE6EE",
    link: "#25A8ED",
    linkHover: "#0086B8",
    success: "#4CAF50",
    error: "#D84315",
    warning: "#EF753E",
  },
  light: {
    bg: "#F9F9F9",
    sb: "#FFFFFF",
    sbBdr: "#E5E5E5",
    hdr: "#FFFFFF",
    card: "#FFFFFF",
    cardBdr: "#E5E5E5",
    text: "#333333",
    mid: "#666666",
    sub: "#878787",
    hover: "#F5F5F5",
    divider: "#E5E5E5",
    activeBg: "#FFE6EE",
    sbText: "#333333",
    hdrText: "#333333",
  },
  dark: {
    bg: "#141414",
    sb: "#1A1A1A",
    sbBdr: "#2E2E2E",
    hdr: "#1A1A1A",
    card: "#222222",
    cardBdr: "#333333",
    text: "#F5F5F5",
    mid: "#AAAAAA",
    sub: "#878787",
    hover: "#2A2A2A",
    divider: "#333333",
    activeBg: "#3D1525",
    sbText: "#F5F5F5",
    hdrText: "#F5F5F5",
  },
  chrome: {
    radiusSm: "6px",
    radiusMd: "8px",
    radiusLg: "12px",
    shadowSm: "0 1px 2px rgba(0,0,0,0.04)",
    shadowLg: "0 4px 16px rgba(0,0,0,0.08)",
    shadowPopover: "0 8px 32px rgba(0,0,0,0.12)",
  },
  typography: DEFAULT_TYPOGRAPHY,
};

export const BGAUSS_THEME = {
  id: "bgauss",
  name: "BGauss",
  accent: { name: "BGauss", hex: "#78B466", activeBg: "#E8F5E3" },
  brand: {
    primary: "#78B466",
    primaryHover: "#5FA34F",
    primaryDark: "#4A8F3D",
    primaryTint: "#E8F5E3",
    link: "#0A2A53",
    linkHover: "#143D6B",
    success: "#0A9B3F",
    error: "#C13232",
    warning: "#F3C101",
  },
  light: {
    bg: "#F6F7F5",
    sb: "#FFFFFF",
    sbBdr: "#E5E5E5",
    hdr: "#FFFFFF",
    card: "#FFFFFF",
    cardBdr: "#E0E0E0",
    text: "#434343",
    mid: "#666666",
    sub: "#929292",
    hover: "#F2F4F0",
    divider: "#E5E5E5",
    activeBg: "#E8F5E3",
    sbText: "#434343",
    hdrText: "#434343",
  },
  dark: {
    bg: "#0A1628",
    sb: "#0A2A53",
    sbBdr: "#143D6B",
    hdr: "#0A2A53",
    card: "#122D52",
    cardBdr: "#1E4A7A",
    text: "#F5F7FA",
    mid: "#A8B8C8",
    sub: "#7A8FA3",
    hover: "#143D6B",
    divider: "#1E4A7A",
    activeBg: "#1A3D2A",
    sbText: "#F5F7FA",
    hdrText: "#F5F7FA",
  },
  chrome: {
    radiusSm: "6px",
    radiusMd: "10px",
    radiusLg: "14px",
    shadowSm: "0 1px 3px rgba(10, 42, 83, 0.06)",
    shadowLg: "0 6px 20px rgba(10, 42, 83, 0.1)",
    shadowPopover: "0 10px 36px rgba(10, 42, 83, 0.14)",
  },
  typography: {
    googleFontsUrl:
      "https://fonts.googleapis.com/css2?family=Montserrat:wght@300;400;500;600;700&display=swap",
    fontFamily: "'Montserrat', system-ui, -apple-system, sans-serif",
    fontFamilyDisplay: "'Montserrat', system-ui, -apple-system, sans-serif",
    fontFamilyMono: "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace",
  },
};

export const BRAND_THEMES = {
  intellicar: INTELICAR_THEME,
  bgauss: BGAUSS_THEME,
};

export function resolveBrandTheme(accent) {
  const id =
    accent?.brandThemeId ??
    (accent?.brandTheme === true ? "intellicar" : null) ??
    (accent?.name === "Intellicar" ? "intellicar" : accent?.name === "BGauss" ? "bgauss" : null);
  return id ? BRAND_THEMES[id] ?? null : null;
}

export function buildBrandColors(theme, dark) {
  const palette = dark ? theme.dark : theme.light;
  const { brand } = theme;
  return {
    ...palette,
    aLight: palette.activeBg,
    link: brand.link,
    linkHover: brand.linkHover,
    primaryHover: brand.primaryHover,
    primaryDark: brand.primaryDark,
    success: brand.success,
    error: brand.error,
    warning: brand.warning,
  };
}

let activeFontLinkId = "nm-brand-font";

export function applyBrandThemeToDocument(theme, dark, c) {
  const { brand, chrome, typography } = theme;
  const accentHex = theme.accent.hex;
  const r = document.documentElement;

  r.style.setProperty("--nm-accent", accentHex);
  r.style.setProperty("--nm-accent-hover", brand.primaryHover);
  r.style.setProperty("--nm-accent-tint", brand.primaryTint);
  r.style.setProperty("--nm-link", brand.link);
  r.style.setProperty("--nm-link-hover", brand.linkHover);
  r.style.setProperty("--nm-text", c.text);
  r.style.setProperty("--nm-mid", c.mid);
  r.style.setProperty("--nm-sub", c.sub);
  r.style.setProperty("--nm-bg", c.bg);
  r.style.setProperty("--nm-card", c.card);
  r.style.setProperty("--nm-line", c.cardBdr);
  r.style.setProperty("--nm-hover", c.hover);
  r.style.setProperty("--nm-divider", c.divider);
  r.style.setProperty("--nm-hdr", c.hdr);
  r.style.setProperty("--nm-hdr-text", c.hdrText ?? c.text);
  r.style.setProperty("--nm-sb", c.sb);
  r.style.setProperty("--nm-sb-text", c.sbText ?? c.text);
  r.style.setProperty("--nm-sb-bdr", c.sbBdr);
  r.style.setProperty("--nm-card-bdr", c.cardBdr);
  r.style.setProperty("--nm-radius-sm", chrome.radiusSm);
  r.style.setProperty("--nm-radius-md", chrome.radiusMd);
  r.style.setProperty("--nm-radius-lg", chrome.radiusLg);
  r.style.setProperty("--nm-shadow", dark ? "none" : chrome.shadowSm);
  r.style.setProperty("--nm-shadow-lg", dark ? "0 12px 32px rgba(0,0,0,0.55)" : chrome.shadowLg);
  r.style.setProperty("--nm-shadow-popover", dark ? "0 12px 40px rgba(0,0,0,0.6)" : chrome.shadowPopover);
  r.style.setProperty("--nm-font", typography.fontFamily);
  r.style.setProperty("--nm-font-display", typography.fontFamilyDisplay);
  r.style.setProperty("--nm-font-mono", typography.fontFamilyMono);
  r.style.colorScheme = dark ? "dark" : "light";

  document.body.style.fontFamily = typography.fontFamily;
  document.body.style.background = c.bg;
  document.body.style.color = c.text;

  let link = document.getElementById(activeFontLinkId);
  if (!link) {
    link = document.createElement("link");
    link.id = activeFontLinkId;
    link.rel = "stylesheet";
    document.head.appendChild(link);
  }
  if (link.href !== typography.googleFontsUrl) {
    link.href = typography.googleFontsUrl;
  }
}
